// Community API for managing topics, threads, and comments
// Updated to use real HTTP calls instead of mock data
// Features:
// - Complete CRUD operations for Topics, Threads, and Comments
// - TypeScript interfaces for type safety
// - Enhanced error handling with specific error messages
// - Request/response caching for better performance
// - Uses existing axios instances from api.tsx

import { api, noMonitor, monitoredRequest } from './api';

// TypeScript Interfaces
export interface User {
  _id: string;
  name: string;
  surname: string;
  role?: 'User' | 'Moderator' | 'Admin';
}

export interface Topic {
  _id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  threadsCount: number;
  postsCount: number;
  lastActivity: string;
  lastPost?: {
    user: User;
    createdAt: string;
  };
  isSticky: boolean;
  isLocked: boolean;
  createdBy: User;
  tags?: string[];
  visibility?: 'public' | 'private' | 'members';
  createdAt: string;
  updatedAt?: string;
}

export interface Thread {
  _id: string;
  title: string;
  content: string;
  topicId: string;
  replies: number;
  views: number;
  likes: number;
  likedBy?: string[];
  isSticky: boolean;
  isLocked: boolean;
  isSolved: boolean;
  solvedBy?: User;
  solvedAt?: string;
  tags: string[];
  createdBy: User;
  status: 'open' | 'closed' | 'solved';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  lastActivity: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Comment {
  _id: string;
  content: string;
  threadId: string;
  parentId?: string;
  depth: number;
  likes: number;
  likedBy?: string[];
  replies: string[];
  createdBy: User;
  isEdited: boolean;
  editedAt?: string;
  isDeleted: boolean;
  deletedAt?: string;
  isBestAnswer: boolean;
  markedAsBestBy?: User;
  createdAt: string;
  updatedAt?: string;
}

export interface CommunityStats {
  totalTopics: number;
  totalThreads: number;
  totalComments: number;
  activeUsers: number;
  trending: {
    topics: Topic[];
    threads: Thread[];
  };
}

// Cache utility for better performance (optional)
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Enhanced error handling utility
const handleApiError = (error: any, context: string) => {
  console.error(`Error in ${context}:`, error);
  
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    switch (status) {
      case 400:
        throw new Error(data.message || 'Invalid request');
      case 401:
        throw new Error('Unauthorized - Please log in');
      case 403:
        throw new Error('Forbidden - Insufficient permissions');
      case 404:
        throw new Error('Resource not found');
      case 429:
        throw new Error('Too many requests - Please try again later');
      case 500:
        throw new Error('Server error - Please try again');
      default:
        throw new Error(data.message || `Request failed with status ${status}`);
    }
  } else if (error.request) {
    // Request was made but no response received
    throw new Error('Network error - Please check your connection');
  } else {
    // Something else happened
    throw new Error('An unexpected error occurred');
  }
};

// Topics API
export const getTopics = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'recent' | 'popular' | 'alphabetical';
}): Promise<{
  topics: Topic[];
  total: number;
  page: number;
  pages: number;
}> => {
  try {
    const cacheKey = `topics-${JSON.stringify(params)}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get('/community/topics', { params });
    const data = response.data;
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    handleApiError(error, 'getTopics');
    throw error; // Re-throw after handling
  }
};

export const getTopic = async (topicId: string): Promise<Topic> => {
  try {
    const cacheKey = `topic-${topicId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get(`/community/topics/${topicId}`);
    const data = response.data;
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    handleApiError(error, 'getTopic');
    throw error;
  }
};

export const createTopic = async (data: {
  title: string;
  description: string;
  icon?: string;
  color?: string;
  tags?: string[];
}): Promise<Topic> => {
  try {
    const response = await api.post('/community/topics', data);
    
    // Clear related cache entries
    for (const key of cache.keys()) {
      if (key.startsWith('topics-')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'createTopic');
    throw error;
  }
};

// Threads API
export const getThreads = async (params?: {
  topicId?: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'recent' | 'popular' | 'oldest' | 'unanswered';
  status?: 'open' | 'closed' | 'solved';
}): Promise<{
  threads: any[];
  total: number;
  page: number;
  pages: number;
}> => {
  try {
    const cacheKey = `threads-${JSON.stringify(params)}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get('/community/threads', { params });
    const data = response.data;
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    handleApiError(error, 'getThreads');
    throw error;
  }
};

export const getThread = async (threadId: string): Promise<any> => {
  try {
    const cacheKey = `thread-${threadId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get(`/community/threads/${threadId}`);
    const data = response.data.data;
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    handleApiError(error, 'getThread');
    throw error;
  }
};

export const createThread = async (data: {
  title: string;
  content: string;
  topicId: string;
  tags?: string[];
  isSticky?: boolean;
}): Promise<any> => {
  try {
    const response = await monitoredRequest.post('/community/threads', data);
    
    // Clear related cache entries
    for (const key of cache.keys()) {
      if (key.startsWith('threads-') || key.startsWith('topics-')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'createThread');
    throw error;
  }
};

export const likeThread = async (threadId: string): Promise<{ liked: boolean; likes: number }> => {
  try {
    const response = await api.post(`/community/threads/${threadId}/like`);
    
    // Clear thread cache
    cache.delete(`thread-${threadId}`);
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'likeThread');
    throw error;
  }
};

// Comments API
export const getComments = async (threadId: string, params?: {
  page?: number;
  limit?: number;
  sortBy?: 'recent' | 'oldest' | 'popular';
}): Promise<{
  comments: any[];
  total: number;
  page: number;
  pages: number;
}> => {
  try {
    const cacheKey = `comments-${threadId}-${JSON.stringify(params)}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get(`/community/threads/${threadId}/comments`, { params });
    const data = response.data;
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    handleApiError(error, 'getComments');
    throw error;
  }
};

export const createComment = async (data: {
  content: string;
  threadId: string;
  parentId?: string;
}): Promise<any> => {
  try {
    const response = await monitoredRequest.post(`/community/threads/${data.threadId}/comments`, {
      content: data.content,
      parentId: data.parentId,
    });
    
    // Clear comments cache for this thread
    for (const key of cache.keys()) {
      if (key.startsWith(`comments-${data.threadId}-`)) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'createComment');
    throw error;
  }
};

// Community Stats
export const getCommunityStats = async (): Promise<any> => {
  try {
    const cacheKey = 'community-stats';
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get('/api/community/stats');
    const data = response.data;
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    handleApiError(error, 'getCommunityStats');
    throw error;
  }
};

// Search
// Search API
export const searchCommunityContent = async (params: {
  query: string;
  type?: 'topics' | 'threads' | 'all';
  page?: number;
  limit?: number;
}): Promise<{
  results: any[];
  total: number;
  page: number;
  pages: number;
}> => {
  try {
    const cacheKey = `search-${JSON.stringify(params)}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get('/api/community/search', { params });
    const data = response.data;
    
    setCachedData(cacheKey, data); // Cache search results
    return data;
  } catch (error) {
    handleApiError(error, 'searchCommunityContent');
    throw error;
  }
};

// User API
export const getUserProfile = async (userId: string): Promise<any> => {
  try {
    const cacheKey = `user-profile-${userId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await noMonitor.get(`/api/community/users/${userId}`);
    const data = response.data;
    
    setCachedData(cacheKey, data); // Cache user profile
    return data;
  } catch (error) {
    handleApiError(error, 'getUserProfile');
    throw error;
  }
};

export const updateUserProfile = async (data: {
  bio?: string;
  avatar?: string;
  name?: string;
}): Promise<any> => {
  try {
    const response = await monitoredRequest.put('/api/community/users/profile', data);
    
    // Clear user profile cache
    const userId = response.data.id;
    if (userId) {
      cache.delete(`user-profile-${userId}`);
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateUserProfile');
    throw error;
  }
};

// Additional API functions
export const updateTopic = async (topicId: string, data: Partial<{
  title: string;
  description: string;
  icon: string;
  color: string;
  tags: string[];
  isSticky: boolean;
  isLocked: boolean;
}>): Promise<any> => {
  try {
    const response = await monitoredRequest.patch(`/api/community/topics/${topicId}`, data);
    
    // Clear related cache
    cache.delete(`topic-${topicId}`);
    for (const key of cache.keys()) {
      if (key.startsWith('topics-')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateTopic');
    throw error;
  }
};

export const deleteTopic = async (topicId: string): Promise<void> => {
  try {
    await monitoredRequest.delete(`/api/community/topics/${topicId}`);
    
    // Clear related cache
    cache.delete(`topic-${topicId}`);
    for (const key of cache.keys()) {
      if (key.startsWith('topics-') || key.startsWith('threads-')) {
        cache.delete(key);
      }
    }
  } catch (error) {
    handleApiError(error, 'deleteTopic');
    throw error;
  }
};

export const updateThread = async (threadId: string, data: Partial<{
  title: string;
  content: string;
  tags: string[];
  isSticky: boolean;
  isLocked: boolean;
  status: string;
}>): Promise<any> => {
  try {
    const response = await monitoredRequest.patch(`/api/community/threads/${threadId}`, data);
    
    // Clear related cache
    cache.delete(`thread-${threadId}`);
    for (const key of cache.keys()) {
      if (key.startsWith('threads-')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateThread');
    throw error;
  }
};

export const deleteThread = async (threadId: string): Promise<void> => {
  try {
    await monitoredRequest.delete(`/api/community/threads/${threadId}`);
    
    // Clear related cache
    cache.delete(`thread-${threadId}`);
    for (const key of cache.keys()) {
      if (key.startsWith('threads-') || key.startsWith('comments-')) {
        cache.delete(key);
      }
    }
  } catch (error) {
    handleApiError(error, 'deleteThread');
    throw error;
  }
};

export const markThreadSolved = async (threadId: string): Promise<any> => {
  try {
    const response = await monitoredRequest.post(`/api/community/threads/${threadId}/solve`);
    
    // Clear related cache
    cache.delete(`thread-${threadId}`);
    for (const key of cache.keys()) {
      if (key.startsWith('threads-')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'markThreadSolved');
    throw error;
  }
};

export const updateComment = async (commentId: string, data: {
  content: string;
}): Promise<any> => {
  try {
    const response = await monitoredRequest.patch(`/api/community/comments/${commentId}`, data);
    
    // Clear related cache
    for (const key of cache.keys()) {
      if (key.includes('comment')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateComment');
    throw error;
  }
};

export const deleteComment = async (commentId: string): Promise<void> => {
  try {
    await monitoredRequest.delete(`/api/community/comments/${commentId}`);
    
    // Clear related cache
    for (const key of cache.keys()) {
      if (key.includes('comment')) {
        cache.delete(key);
      }
    }
  } catch (error) {
    handleApiError(error, 'deleteComment');
    throw error;
  }
};

export const likeComment = async (commentId: string): Promise<{ liked: boolean; likes: number }> => {
  try {
    const response = await monitoredRequest.post(`/api/community/comments/${commentId}/like`);
    
    // Clear related cache
    for (const key of cache.keys()) {
      if (key.includes('comment')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'likeComment');
    throw error;
  }
};

export const markBestAnswer = async (commentId: string): Promise<any> => {
  try {
    const response = await monitoredRequest.post(`/api/community/comments/${commentId}/best-answer`);
    
    // Clear related cache
    for (const key of cache.keys()) {
      if (key.includes('comment') || key.includes('thread')) {
        cache.delete(key);
      }
    }
    
    return response.data;
  } catch (error) {
    handleApiError(error, 'markBestAnswer');
    throw error;
  }
};

// Cache management
export const clearAllCache = (): void => {
  cache.clear();
};

// Default export with all functions
export default {
  getTopics,
  getTopic,
  createTopic,
  updateTopic,
  deleteTopic,
  getThreads,
  getThread,
  createThread,
  updateThread,
  deleteThread,
  likeThread,
  markThreadSolved,
  getComments,
  createComment,
  updateComment,
  deleteComment,
  likeComment,
  markBestAnswer,
  getCommunityStats,
  searchCommunityContent,
  getUserProfile,
  updateUserProfile,
  clearAllCache,
};
